import React from 'react';
import { formatDate } from '../../lib/utils';
import {
  CalendarBlank,
  CheckCircle,
  Plus,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import RateRow from './RateRow';

/**
 * Single payer-contract card inside the accordion. Header shows status +
 * dates + actions; body shows the rates table or a placeholder.
 */
const ContractCard = ({
  contract, editingRate, setEditingRate, savingRate,
  onAddRate, onToggleStatus, onDeleteContract,
  onStartEditRate, onSaveRate, onDeleteRate,
}) => (
  <Card
    className={`border ${contract.status === 'Active' ? 'border-emerald-200' : 'border-slate-200 opacity-75'}`}
  >
    <CardHeader className="pb-2 flex flex-row items-center justify-between">
      <div className="flex items-center gap-3">
        <Badge
          className={
            contract.status === 'Active'
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : 'bg-slate-100 text-slate-500 border-slate-200'
          }
          data-testid={`contract-status-${contract.id}`}
        >
          {contract.status}
        </Badge>
        <div>
          <CardTitle className="text-base">{contract.contract_name}</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <CalendarBlank size={12} />
            {formatDate(contract.start_date)}
            {contract.end_date ? ` — ${formatDate(contract.end_date)}` : ' — Ongoing'}
          </p>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm" variant="ghost"
          onClick={() => onAddRate(contract.id)}
          className="text-indigo-600 h-8"
          data-testid={`add-rate-${contract.id}`}
        >
          <Plus size={14} className="mr-1" /> Add Code
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => onToggleStatus(contract)}
          className="h-8"
          data-testid={`toggle-contract-${contract.id}`}
        >
          {contract.status === 'Active'
            ? <XCircle size={14} className="mr-1" />
            : <CheckCircle size={14} className="mr-1" />}
          {contract.status === 'Active' ? 'Expire' : 'Activate'}
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => onDeleteContract(contract.id)}
          className="text-red-500 h-8"
          data-testid={`delete-contract-${contract.id}`}
        >
          <Trash size={14} />
        </Button>
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      {contract.rates?.length > 0 ? (
        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-24 font-semibold">HCPCS</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="w-20 font-semibold">Unit</TableHead>
                <TableHead className="w-28 font-semibold">Unit Rate</TableHead>
                <TableHead className="w-28 font-semibold">Base Rate</TableHead>
                <TableHead className="w-40 font-semibold">Modifiers</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contract.rates.map((rate) => (
                <RateRow
                  key={rate.hcpcs_code}
                  rate={rate}
                  contractId={contract.id}
                  editingRate={editingRate}
                  setEditingRate={setEditingRate}
                  savingRate={savingRate}
                  onStartEdit={onStartEditRate}
                  onSaveRate={onSaveRate}
                  onDeleteRate={onDeleteRate}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-4">No billing codes in this contract</p>
      )}
    </CardContent>
  </Card>
);

export default ContractCard;
